import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetHeader,
  SheetFooter,
  SheetTitle,
  SheetClose,
} from "@/components/ui/sheet";
import { useTRPC } from "@/trpc";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useMemo, useRef } from "react";
import InlineViewer from "./inline-viewer";
import { FormProvider, useFormContext } from "../../forms/form-editor/context";
import { TFormSubmission } from "@/types/trpc.types";
import { FieldTypeName } from "../../forms/form-editor/fields";
import { Label } from "@/components/ui/label";

interface Props {
  task_id: string;
  disabled?: boolean;
  trigger?: React.ReactNode;
}

const ViewTaskSubmission = ({ task_id, trigger, disabled }: Props) => {
  const { trpc } = useTRPC();
  const [open, setOpen] = React.useState(false);
  const { loadInitialResponses } = useFormContext();
  const hydratedKeyRef = useRef<string | null>(null);

  const {
    data: task,
    isLoading: isLoadingTask,
    isError,
    error,
  } = useQuery({
    queryKey: ["tasks", task_id],
    queryFn: async () => trpc.tasks.getById.query({ task_id }),
    enabled: !!open,
  });

  const formFromTask = task?.report?.form;
  const submissionFromTask = useMemo(() => {
    const subs = formFromTask?.form_submissions ?? [];
    return subs[0] ?? undefined; // the backend already filtered these
  }, [formFromTask]);

  const fieldById = useMemo(() => {
    const map = new Map<string, any>();
    for (const f of formFromTask?.fields ?? []) map.set(f.id, f);
    return map;
  }, [formFromTask]);

  const inlineViewerData = useMemo(() => {
    if (!formFromTask || !submissionFromTask) return undefined;

    const responsesWithField = (submissionFromTask.responses ?? []).map((r: any) => ({
      ...r,
      created_at: r.created_at ? new Date(r.created_at) : null,
      field_id: r.field_id ?? "",
      submission_id: r.submission_id ?? submissionFromTask.id,
      // attach full field object (or null) as InlineViewer expects
      field: r.field ?? fieldById.get(r.field_id ?? "") ?? null,
    }));

    return {
      id: formFromTask.id,
      name: formFromTask.name,
      description: formFromTask.description,
      created_at: formFromTask.created_at ? new Date(formFromTask.created_at) : null,
      updated_at: formFromTask.updated_at ? new Date(formFromTask.updated_at) : null,
      template: formFromTask.template,
      anonymous: formFromTask.anonymous,
      public: formFromTask.public,
      expiry_date: formFromTask.expiry_date ? new Date(formFromTask.expiry_date) : null,
      fields: formFromTask.fields ?? [],
      submission: {
        id: submissionFromTask.id,
        form_id: submissionFromTask.form_id,
        employee_id: submissionFromTask.employee_id,
        submitted_at: submissionFromTask.submitted_at
          ? new Date(submissionFromTask.submitted_at)
          : null,
        responses: responsesWithField,
      },
    } as unknown as TFormSubmission; // matches InlineViewer prop type
  }, [formFromTask, submissionFromTask, fieldById]);

  const groupedResponses = useMemo(() => {
    const responses = submissionFromTask?.responses ?? [];
    if (!responses.length) return null;

    return responses.map((r: any) => ({
      fieldId: r.field_id ?? "",
      value: r.response_value ?? "",
      // ensure non-undefined string to satisfy FieldResponse
      fieldType: (fieldById.get(r.field_id ?? "")?.type as FieldTypeName) ?? "text",
    }));
  }, [submissionFromTask, fieldById]);

  useEffect(() => {
    if (!open) {
      hydratedKeyRef.current = null;
      return;
    }
    if (!groupedResponses) return;

    const key = task_id + "::" + (submissionFromTask?.id ?? "no-submission");
    if (hydratedKeyRef.current === key) return;

    loadInitialResponses(groupedResponses);
    hydratedKeyRef.current = key;
  }, [open, groupedResponses, task_id, submissionFromTask?.id]);

  return (
    <Sheet open={disabled ? false : open} onOpenChange={setOpen}>
      <SheetTrigger asChild disabled={disabled}>{trigger}</SheetTrigger>
      <SheetContent className="md:max-w-7xl">
        <SheetHeader>
          <SheetTitle>Taak resultaat</SheetTitle>
        </SheetHeader>

        <div className="h-full overflow-auto px-4">
          {isLoadingTask && (
            <p className="my-2 text-center text-sm text-muted-foreground">
              Bezig met laden...
            </p>
          )}

          {isError && (
            <p className="my-2 text-center text-sm text-destructive">
              Kon taak niet laden: {(error as Error)?.message ?? "Onbekende fout"}
            </p>
          )}

          {!isLoadingTask && !isError && (
            <div className="flex flex-col gap-4">
              <div className="rounded-lg border bg-muted p-4">
                <p className="text-sm font-medium uppercase text-muted-foreground">
                  {task?.report?.type?.join(" - ")}
                </p>
                <p className="text-2xl font-bold">{task?.mongo_player?.name}</p>
                {task?.note && (
                  <p className="text-sm font-medium text-foreground">
                    Notitie: {task?.note}
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-8 rounded-lg border bg-muted p-2">
                <InlineViewer form_submission={inlineViewerData} />
                {!inlineViewerData && (
                  <p className="text-sm text-muted-foreground px-2">
                    Geen inzending gevonden voor deze taak.
                  </p>
                )}
              </div>

              <div className="flex flex-col gap-8 rounded-lg border bg-muted p-2">
                <div>
                  <Label>Feedback</Label>
                  <p>{task?.feedback ?? "-"}</p>
                </div>
              </div>
            </div>
          )}
        </div>

        <SheetFooter>
          <SheetClose>Sluiten</SheetClose>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default function ViewTaskSubmissionWrapper(props: Props) {
  return (
    <FormProvider>
      <ViewTaskSubmission {...props} />
    </FormProvider>
  );
}
